import { t } from "./i18n";
import { Fragment } from "react";
import styled from "styled-components";
import { FiBarChart2, FiLayers, FiZap, FiTrendingUp, FiLogOut, FiCheck } from "react-icons/fi";
import { renderWithFileRefs, type FilesMap } from "./BlockRenderer";
import { WHITE, TEXT_LEAD, TINT_GREEN, TINT_AMBER, GREEN_ACCENT, INK, BORDER_SUBTLE, SEMAPHORE, SURFACE_CARD, TEXT_VALUE, TONE } from "../tokens";

export interface DefinitionRowData {
  label: string;
  text: string;
  // Amber label — e.g. "Tenencia y deberes", "Viabilidad real": something
  // the reader needs to act on, not a plain informational row.
  alert?: boolean;
  tone?: typeof TONE.amber | typeof TONE.red;
}

export interface SensitivityColumn {
  label: string;
  highlight?: boolean;
  highlightTone?: typeof TONE.amber;
}

export interface SensitivityRow {
  label: string;
  values: string[];
  // "muted": the intermediate cost rows (greyed out). "total": the closing
  // "= Valor del suelo" row (bold label, per-column semaphore via `colors`).
  variant?: "muted" | "total";
  colors?: (string | undefined)[];
}

export interface SensitivityTableData {
  headerLabel: string;
  columns: SensitivityColumn[];
  rows: SensitivityRow[];
  footnote?: string;
}

export interface AdvantageItem {
  title: string;
  text: string;
}

export interface ExitStrategyItem {
  letter: string;
  text: string;
}

export interface OperationPanelsData {
  economicImpact?: { rows: DefinitionRowData[] } | null;
  // More than one table when the report weighs scenarios side by side
  // (Taracena: the logistic one it rules out above the light-industrial one it
  // takes as reference); each paints its own footnote, if any.
  developmentPotential?: { rows: DefinitionRowData[]; sensitivity?: SensitivityTableData | SensitivityTableData[] | null } | null;
  advantages?: { items: AdvantageItem[] } | null;
  potentialReturn?: {
    text: string;
    exitStrategy?: { items: ExitStrategyItem[]; note?: string } | null;
  } | null;
}

const PanelsGrid = styled.div`
  display: grid;
  // Fixed 2 columns, not auto-fit: the design's own content column is capped
  // to a max width (~1040px), so its auto-fit/minmax(340px,1fr) grid always
  // resolves to exactly 2 columns in practice — we have no such cap here, so
  // a fluid auto-fit would spread these 4 panels across 3 columns on wide
  // screens, breaking the intended row pairing (Impacto económico +
  // Potencial de desarrollo / Ventajas + Retorno potencial).
  grid-template-columns: repeat(2, minmax(0, 1fr));
  row-gap: 2.5rem;
  column-gap: 2rem;
  margin: 1.75rem 0 1.25rem;

  // A dedicated (larger) breakpoint, not the app's shared theme.breakpoints.md
  // (768px, tied to when the sidebar itself hides): with the sidebar's fixed
  // 240px still present, the developmentPotential sensitivity table (7
  // columns) starts clipping past its column's right edge around a ~1024px
  // viewport. Stack to 1 column earlier, with margin, so it never clips.
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1rem;
`;

const PanelIconBadge = styled.span`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  border-radius: 0.56rem;
  background: ${TINT_GREEN[200]};
  color: ${GREEN_ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: ${TEXT_VALUE};
`;

// ── Tabla horizontal (definition rows) ───────────────────────────
const DefinitionRows = styled.div`
  display: flex;
  flex-direction: column;

  > div:last-child {
    border-bottom: 1px solid ${BORDER_SUBTLE};
  }
`;

const DefinitionRow = styled.div`
  display: grid;
  grid-template-columns: 7.4rem 1fr;
  gap: 1.125rem;
  padding: 0.8rem 0;
  border-top: 1px solid ${BORDER_SUBTLE};
  align-items: baseline;
`;

const definitionLabelColor = (tone?: DefinitionRowData["tone"], alert?: boolean) =>
  tone ? SEMAPHORE[tone].text : alert ? SEMAPHORE.amber.text : TEXT_LEAD;

const DefinitionLabel = styled.span<{ $alert?: boolean; $tone?: DefinitionRowData["tone"] }>`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${(p) => definitionLabelColor(p.$tone, p.$alert)};
`;

const DefinitionText = styled.span`
  font-size: 0.844rem;
  line-height: 1.55;
  color: ${INK.soft};

  strong {
    color: ${TEXT_LEAD};
    font-weight: 700;
  }
`;

const renderDefinitionRows = (rows: DefinitionRowData[], files?: FilesMap) => (
  <DefinitionRows>
    {rows.map((row, i) => (
      <DefinitionRow key={i}>
        <DefinitionLabel $alert={row.alert} $tone={row.tone}>{row.label}</DefinitionLabel>
        <DefinitionText>{renderWithFileRefs(row.text, files)}</DefinitionText>
      </DefinitionRow>
    ))}
  </DefinitionRows>
);

// ── Tabla normal (sensitivity table) + subtexto ──────────────────
const SensitivityWrap = styled.div`
  margin-top: 1rem;
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.75rem;
  overflow: hidden;
`;

const SensitivityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SensitivityHeadRow = styled.tr`
  border-bottom: 1px solid ${BORDER_SUBTLE};
`;

const SensitivityHeadCell = styled.th<{ $highlight?: boolean; $amber?: boolean }>`
  text-align: center;
  padding: 0.625rem 0.375rem;
  font-size: 0.78rem;
  font-weight: ${(p) => (p.$highlight ? 800 : 600)};
  color: ${(p) => (p.$highlight ? TEXT_VALUE : INK.muted)};
  background: ${(p) => (!p.$highlight ? "transparent" : p.$amber ? TINT_AMBER : TINT_GREEN[200])};

  &:first-child {
    text-align: left;
    font-weight: 600;
    color: ${INK.muted};
    background: transparent;
  }
`;

const SensitivityRowEl = styled.tr`
  border-top: 1px solid ${BORDER_SUBTLE};
`;

const SensitivityLabelCell = styled.td<{ $total?: boolean }>`
  text-align: left;
  padding: ${(p) => (p.$total ? "0.625rem 0.75rem" : "0.56rem 0.75rem")};
  font-size: ${(p) => (p.$total ? "0.78rem" : "0.75rem")};
  font-weight: ${(p) => (p.$total ? 700 : 400)};
  color: ${(p) => (p.$total ? TEXT_LEAD : INK.soft)};
`;

const SensitivityValueCell = styled.td<{ $highlight?: boolean; $amber?: boolean; $muted?: boolean; $total?: boolean; $color?: string }>`
  text-align: center;
  padding: ${(p) => (p.$total ? "0.625rem 0.375rem" : "0.56rem 0.375rem")};
  font-size: ${(p) => (p.$total ? "0.844rem" : "0.8125rem")};
  font-weight: ${(p) => (p.$total ? 700 : p.$muted ? 400 : 400)};
  color: ${(p) => p.$color ?? (p.$muted ? INK.quiet : TEXT_LEAD)};
  background: ${(p) => (!p.$highlight ? "transparent" : p.$amber ? TINT_AMBER : p.$total ? TINT_GREEN[200] : TINT_GREEN[50])};
`;

const SensitivityFootnote = styled.div`
  margin-top: 0.56rem;
  font-size: 0.66rem;
  color: ${INK.faint};
  line-height: 1.5;
`;

const renderSensitivityTable = (data: SensitivityTableData) => (
  <>
    <SensitivityWrap>
      <SensitivityTable>
        <thead>
          <SensitivityHeadRow>
            <SensitivityHeadCell as="th">{data.headerLabel}</SensitivityHeadCell>
            {data.columns.map((col, i) => (
              <SensitivityHeadCell key={i} $highlight={col.highlight} $amber={col.highlightTone === TONE.amber}>
                {col.label}
              </SensitivityHeadCell>
            ))}
          </SensitivityHeadRow>
        </thead>
        <tbody>
          {data.rows.map((row, ri) => (
            <SensitivityRowEl key={ri}>
              <SensitivityLabelCell $total={row.variant === "total"}>{row.label}</SensitivityLabelCell>
              {row.values.map((value, ci) => (
                <SensitivityValueCell
                  key={ci}
                  $highlight={data.columns[ci]?.highlight}
                  $amber={data.columns[ci]?.highlightTone === TONE.amber}
                  $muted={row.variant === "muted"}
                  $total={row.variant === "total"}
                  $color={row.colors?.[ci]}
                >
                  {value}
                </SensitivityValueCell>
              ))}
            </SensitivityRowEl>
          ))}
        </tbody>
      </SensitivityTable>
    </SensitivityWrap>
    {data.footnote && <SensitivityFootnote>{data.footnote}</SensitivityFootnote>}
  </>
);

// ── Pills (Ventajas de la operación) ─────────────────────────────
const AdvantagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const AdvantageCard = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem 1.06rem;
  background: ${SURFACE_CARD};
  border: 1px solid ${BORDER_SUBTLE};
  border-radius: 0.81rem;
`;

const AdvantageIcon = styled.span`
  flex-shrink: 0;
  margin-top: 0.1rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: ${TINT_GREEN[400]};
  color: ${SEMAPHORE.green.fill};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AdvantageText = styled.div`
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${INK.soft};

  strong {
    color: ${TEXT_LEAD};
    font-weight: 600;
  }
`;

// ── Texto regular + enum a/b/c (Retorno potencial / Estrategia de salida) ─
const ReturnParagraph = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.844rem;
  line-height: 1.6;
  color: ${INK.soft};

  strong {
    color: ${TEXT_LEAD};
    font-weight: 600;
  }
`;

const ExitStrategyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ExitStrategyItemRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const LetterBadge = styled.span`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.44rem;
  background: ${TEXT_VALUE};
  color: ${WHITE};
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExitStrategyText = styled.span`
  padding-top: 0.125rem;
  font-size: 0.844rem;
  line-height: 1.5;
  color: ${INK.soft};
`;

// Shared "subtexto" — used both under the sensitivity table and to close the
// exit-strategy list.
const Subtext = styled.div`
  margin-top: 0.75rem;
  font-size: 0.69rem;
  color: ${INK.faint};
  line-height: 1.5;
`;

interface Props {
  panels: OperationPanelsData;
  files?: FilesMap;
}

// The 4 cockpit-only panels below the KPI band: two share a "tabla
// horizontal" (definition-row) building block (Impacto económico,
// Potencial de desarrollo — the latter also adds the sensitivity table +
// subtexto), the other two share the pills / texto-regular+enum pattern
// (Ventajas de la operación, Retorno potencial — the latter also reuses
// subtexto for its "Estrategia de salida" sub-block). One shared grid
// container, 2 columns.
export const ExecutiveSummaryPanels = ({ panels, files }: Props) => {
  const title = (key: string) => t(`dueDiligence.legalFiscalReport.executiveSummaryPanels.${key}`);
  const { economicImpact, developmentPotential, advantages, potentialReturn } = panels;
  if (!economicImpact && !developmentPotential && !advantages && !potentialReturn) return null;

  return (
    <PanelsGrid>
      {economicImpact && (
        <div>
          <PanelHeader>
            <PanelIconBadge>
              <FiBarChart2 size={15} />
            </PanelIconBadge>
            <PanelTitle>{title("economicImpact")}</PanelTitle>
          </PanelHeader>
          {renderDefinitionRows(economicImpact.rows, files)}
        </div>
      )}

      {developmentPotential && (
        <div>
          <PanelHeader>
            <PanelIconBadge>
              <FiLayers size={15} />
            </PanelIconBadge>
            <PanelTitle>{title("developmentPotential")}</PanelTitle>
          </PanelHeader>
          {renderDefinitionRows(developmentPotential.rows, files)}
          {developmentPotential.sensitivity &&
            ([] as SensitivityTableData[]).concat(developmentPotential.sensitivity).map((table, i) => (
              <Fragment key={i}>{renderSensitivityTable(table)}</Fragment>
            ))}
        </div>
      )}

      {advantages && (
        <div>
          <PanelHeader>
            <PanelIconBadge>
              <FiZap size={15} />
            </PanelIconBadge>
            <PanelTitle>{title("operationAdvantages")}</PanelTitle>
          </PanelHeader>
          <AdvantagesList>
            {advantages.items.map((item, i) => (
              <AdvantageCard key={i}>
                <AdvantageIcon>
                  <FiCheck size={10} />
                </AdvantageIcon>
                <AdvantageText>
                  <strong>{item.title}:</strong> {renderWithFileRefs(item.text, files)}
                </AdvantageText>
              </AdvantageCard>
            ))}
          </AdvantagesList>
        </div>
      )}

      {potentialReturn && (
        <div>
          <PanelHeader>
            <PanelIconBadge>
              <FiTrendingUp size={15} />
            </PanelIconBadge>
            <PanelTitle>{title("potentialReturn")}</PanelTitle>
          </PanelHeader>
          <ReturnParagraph>{renderWithFileRefs(potentialReturn.text, files)}</ReturnParagraph>

          {potentialReturn.exitStrategy && (
            <>
              <PanelHeader>
                <PanelIconBadge>
                  <FiLogOut size={15} />
                </PanelIconBadge>
                <PanelTitle>{title("exitStrategy")}</PanelTitle>
              </PanelHeader>
              <ExitStrategyList>
                {potentialReturn.exitStrategy.items.map((item, i) => (
                  <ExitStrategyItemRow key={i}>
                    <LetterBadge>{item.letter}</LetterBadge>
                    <ExitStrategyText>{renderWithFileRefs(item.text, files)}</ExitStrategyText>
                  </ExitStrategyItemRow>
                ))}
              </ExitStrategyList>
              {potentialReturn.exitStrategy.note && <Subtext>{potentialReturn.exitStrategy.note}</Subtext>}
            </>
          )}
        </div>
      )}
    </PanelsGrid>
  );
};
